import { 
  Heading, 
  Button, 
  Text,
  Card
} from "@aidron/aidron-ds";

import { i18n } from "../../../../translate/i18n";

import { ModalTemplate, OrderDetailsTable } from "../../../../components/SharedComponents/sharedComponents.index";

import { useSeetingsViewModel } from "../../../../viewModel/useSettingsViewModel";
import { COLUMNS_IDENTIFIERS, USER_ROLES } from "../../../../components/components.constants";

const { t } = i18n;

function ChangePassword({ actions, ctx, uiState }) {

  const { setNewPassword, handleSavePassword, setResultMessage } = actions || {};
  const { newPassword } = ctx || {};
  const { passValidationRules, resultMessage, isLoading } = uiState || {};

  const ruleItem = (valid, label) => (
    <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px" }}>
      <span style={{ color: valid ? "green" : "red" }}>
        {valid ? "✔" : "✘"}
      </span>
      <span>{label}</span>
    </div>
  );  

  return (
    <div className="mt-3">

      <Heading size="sm">{t("changePassword")}</Heading>

      <div className="mt-3">

        <input
          onChange={e => setNewPassword(e.target.value)}
          type="password"
          placeholder={t("newPassword")}
          value={newPassword}
        />
      </div>

      <div className="mt-2">
        <Text>{t("passRequirements")}</Text>
        {ruleItem(passValidationRules.hasCapitalLetter, t("oneLetterUpperCase"))}
        {ruleItem(passValidationRules.hasLowercaseLetter, t("oneLetterLowerCase"))}
        {ruleItem(passValidationRules.hasNumber, t("oneNumber"))}
        {ruleItem(passValidationRules.hasSpecialCharacter, t("specialCharacter"))}
        {ruleItem(passValidationRules.hasMinimumLength, t("atLeast6Characters"))}
      </div>      

      <Button 
        label={t("savePassword")} 
        onClick={handleSavePassword} 
        disabled={!newPassword || isLoading} 
        className="mt-3" 
      />

      <ModalTemplate 
        open={!!resultMessage}
        width="500px" 
        heading={resultMessage} 
        closingFunction={() => setResultMessage("")} 
      />
    </div>
  );
};

function UploadFile({ actions, ctx, uiState }) {

  const { t } = i18n;

  const { handleSave, handleFileChange } = actions || {};
  const { previewUrl, uploadedFile } = ctx || {};
  const { isLoading, errorMessage } = uiState || {};

  return (
    <div className="mt-5">
      <Heading size="sm">{t("profilePicture")}</Heading>

      <div className="d-flex mt-3">
        <label htmlFor="file-upload" className="aidron-ds-button-styles">
          {t("chooseFile")}
        </label>

        <input
          id="file-upload"
          type="file"
          name="file"
          accept="image/*"
          onChange={handleFileChange}
          className="d-none"
        />
      </div>

      {errorMessage && (
        <Text className="text-danger mt-2">{errorMessage}</Text>
      )}

      {uploadedFile && previewUrl && (
        <div className="d-flex flex-column mt-3">
          <Text className="green-color">{t("fileUploaded")}</Text>

          <img
            src={previewUrl}
            width="100"
            height="100"
            alt={t("profilePicture")}
            style={{
              objectFit: "cover",
              borderRadius: "50%",
              border: "1px solid #ddd"
            }}
          />

          <Button
            label={isLoading ? t("saving") : t("save")}
            onClick={handleSave}
            className="mt-2"
            disabled={isLoading}
          />
        </div>
      )}
    </div>
  );
};

function UsersAvailableInTenantOrg({ ctx }) {

  const { userInTenantOrg = {}, columns } = ctx || {};

  const users = userInTenantOrg?.users ?? [];
  const lastFourUsers = [...users]
  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  .filter((u) => u.role !== "ADMIN")
  .slice(0, 4);


  return (
    <div className="mb-4">
      <Card style={{ padding: "20px" }}>
        <Heading size="sm">{t("lastRegisteredUsers")}</Heading>

        <OrderDetailsTable  
          ctx={{
            columns: columns,
            tableSourceData: lastFourUsers || [],
            tableSourceMetaData: {
              enableSorting: true,
              sortingMode: {
                id: COLUMNS_IDENTIFIERS.CREATED_AT,
                desc: true
              }
            }
          }}
        />

     </Card>
    </div>
  );
};

export function MyAccountSettingsTab() {

  const vm = useSeetingsViewModel({ shouldFetchUserCtx: true });

  return (
    <>
      <Card 
        style={{ 
          margin: "20px 0 10px 0", 
          padding: "20px 20px 20px 30px",
          display: 'grid',
          gridTemplateColumns: "1fr 1fr",
          gridAutoRows: "auto",
          gap: "20px",
        }}>


          <div>
            <ChangePassword 
              actions={{
                setNewPassword: vm.actions.setNewPassword,
                handleSavePassword: vm.actions.handleSavePassword,
                setResultMessage: vm.actions.setResultMessage
              }}
              ctx={{ newPassword: vm.ctx.newPassword }}
              uiState={{
                passValidationRules: vm.uiState.passValidationRules,
                isLoading: vm.uiState.isLoading,
                resultMessage: vm.uiState.resultMessage
              }}
            />

            <UploadFile
              actions={{
                handleSave: vm.actions.handleSave,
                handleFileChange: vm.actions.handleFileChange
              }}
              ctx={{
                previewUrl: vm.ctx.previewUrl,
                uploadedFile: vm.ctx.uploadedFile
              }}
              uiState={{
                isLoading: vm.uiState.isLoading,
                errorMessage: vm.uiState.errorMessage
              }}
            />
          </div>

          {(vm.ctx.userRole === USER_ROLES.TENANT_ADMIN || vm.ctx.userRole === USER_ROLES.ADMIN) && (
            <UsersAvailableInTenantOrg ctx={{ userInTenantOrg: vm.ctx.usersInformation, columns: vm.ctx.columns[0] }}/>
          )}

      </Card>

      <ModalTemplate
        open={!!vm.uiState.showSavedPhotoModal}
        width="500px"
        heading="Foto salva com sucesso!"
        closingFunction={() => vm.actions.setShowSavedPhotoModal(false)}>
        <Text>{t("newPhotoSaved")}</Text>

        <Text className="mt-2">
          A foto do seu perfil foi atualizada e já está sendo exibida em seu perfil.
        </Text>

        <div className="d-flex justify-content-end mt-3">
          <Button 
            label="OK" 
            onClick={() => vm.actions.setShowSavedPhotoModal(false)} 
          />
        </div>
      </ModalTemplate>
    </>
  );
};